<template>
    <page :tabs="tabs" :busy="busy">
        <form-square-switch :sync="true" v-if="settings" class="pricing-toggle ml-2 mt-2" :value="enabled"
            @input="toggleSettings" :labels="{
                checked: 'Beyond Pricing Enabled',
                unchecked: 'Beyond Pricing Disabled',
            }" :color="{
    unchecked: 'rgba(0,0,0,.6)',
}" />
        <div v-if="enabled" class="pricing-toolbar">
            <span class="mr-2">API Key: </span>
            <div class="d-flex api-key-wrapper mt-2 mb-2">
                <b-form-input class="api-key-input" label="API key:" type="text"
                    :value="hideApi ? keyMask : settings.beyond_pricing.key" :readonly="true">
                </b-form-input>
                <responsive-button variant="primary" @click="hideApi = !hideApi" class="show-api-key" title="Show"
                    :icon="['fas', 'eye']" />
                <responsive-button variant="primary" @click="copyToClipboard(settings.beyond_pricing.key)"
                    class="copy-api-key" title="Copy" :icon="['fas', 'copy']" />
            </div>
        </div>
        <grid v-if="enabled" :rowData="items" ref="grid" :columnDefs="columnDefs" :suppressScrollOnNewData="true">
            <responsive-button variant="primary" :icon="['fas', 'arrows-rotate']" @click="fetchBeyondPricing"
                title="Refresh">
                Refresh</responsive-button>
        </grid>
    </page>
</template>

<script>
import API from '../../api';
import Grid from '../generic/grid/grid.vue';
import Page from '../generic/page/page.vue';
import {
    CALENDAR_TAPE_DYNAMIC_PRICING_BEYOND_LIST_ENDPOINT,
    CALENDAR_TAPE_DYNAMIC_PRICING_SETTINGS_ENDPOINT,
    CALENDAR_TAPE_DYNAMIC_PRICING_BEYOND_TOGGLE_PROPERTY_ENDPOINT,
    CALENDAR_TAPE_DYNAMIC_PRICING_BEYOND_TOGGLE_ALL_PROPERTY_ENDPOINT,
    CALENDAR_TAPE_DYNAMIC_PRICING_BEYOND_TOGGLE_SETTINGS_ENDPOINT,
} from '../../endpoints';
import ResponsiveButton from '../generic/responsive-button/responsive-button.vue';
import HeaderComponent from './enable-header-renderer.vue';
import FormSquareSwitch from "../generic/form-square-switch/form-square-switch.vue";
import { formatResponseError } from '../../utils/api';
export default {
    components: { FormSquareSwitch, Page, Grid, ResponsiveButton, agHeaderComponent: HeaderComponent, },
    name: 'DynamicPricingPage',
    data() {
        return {
            items: [],
            busy: null,
            settings: null,
            enabled: null,
            hideApi: true,
            keyMask: '●●●●●●●●●●●●●●●●●●●●●●●●●●●●●●●●●●●●●●●●●●●●●●●●●●●●',
            tabs: [
                {
                    title: 'Beyond Pricing',
                    active: true,
                    route: '/app/pricing/beyond/',
                },
                {
                    title: 'Price Labs',
                    active: false,
                    route: '/app/pricing/labs/',
                },
                {
                    title: 'Logs',
                    active: false,
                    route: '/app/pricing/logs/',
                },
            ]
        };
    },
    mounted() {
        this.fetchData();
    },
    computed: {
        columnDefs() {
            return [
                {
                    field: 'rp_id',
                    headerName: 'ID',
                    maxWidth: 100,
                    sort: 'asc',
                },
                {
                    field: 'name',
                    headerName: 'Property',
                },
                {
                    field: 'enabled',
                    headerName: 'Enabled',
                    headerComponent: 'agHeaderComponent',
                    type: 'switchColumn',
                    resizable: false,
                    maxWidth: 150,
                    minWidth: 150,
                    headerComponentParams: {
                        toggledAll: this.items.every((item) => item.enabled),
                        onToggleAll: async (value) => {
                            try {
                                this.busy = true;
                                const response = await API.post(
                                    CALENDAR_TAPE_DYNAMIC_PRICING_BEYOND_TOGGLE_ALL_PROPERTY_ENDPOINT(),
                                    { enabled: value }
                                );
                                this.fetchBeyondPricing();
                                return response;
                            } catch (e) {
                                this.errorToast({
                                    text: 'Could not update data',
                                });
                            } finally {
                                this.busy = false;
                            }
                        },
                    },
                    cellRendererParams: {
                        onChange: async ({ data, node, api }, value) => {
                            try {
                                await API.post(
                                    CALENDAR_TAPE_DYNAMIC_PRICING_BEYOND_TOGGLE_PROPERTY_ENDPOINT(node.id,),
                                    {

                                        enabled: value,
                                    }
                                );
                                await this.fetchBeyondPricing();
                            } catch (e) {
                                this.errorToast({
                                    text: `Could not save ${data.name}`,
                                });
                                api.redrawRows({
                                    rowNodes: [node],
                                });
                                return;
                            }
                        },
                    },
                },
            ];
        },
    },
    methods: {
        async copyToClipboard(value) {
            try {
                await navigator.clipboard.writeText(value);
                this.successToast({
                    text: 'Copied to clipboard successfully.',
                });
            } catch (err) {
                this.errorToast({
                    text: 'Something went wrong while copying to clipboard.',
                });
            }
        },
        fetchData() {
            this.fetchPricingSettings().then(() => {
                if (this.settings.beyond_pricing.enabled) {
                    this.fetchBeyondPricing();
                }
            });
        },
        async toggleSettings(value) {
            try {
                this.busy = true;
                await API.post(
                    CALENDAR_TAPE_DYNAMIC_PRICING_BEYOND_TOGGLE_SETTINGS_ENDPOINT(),
                    { enabled: value }
                );
                this.enabled = value
                this.fetchData();

            } catch (e) {
                const { generic } = formatResponseError(e)
                this.errorToast({
                    text: generic || 'Could not toggle settings',
                });
            } finally {
                this.busy = false;
            }

        },
        async fetchBeyondPricing() {
            try {
                this.busy = true;
                const { data } = await API.get(
                    CALENDAR_TAPE_DYNAMIC_PRICING_BEYOND_LIST_ENDPOINT()
                );
                this.items = data;
            } catch (e) {
                this.errorToast({ text: 'Could not load data' });
            } finally {
                this.busy = false;
            }
        },
        async fetchPricingSettings() {
            try {
                this.busy = true;
                const { data } = await API.get(
                    CALENDAR_TAPE_DYNAMIC_PRICING_SETTINGS_ENDPOINT()
                );
                this.settings = data.settings;
                this.enabled = data.settings.beyond_pricing.enabled;
            } catch (e) {
                console.log(e);
                this.errorToast({ text: 'Could not load data' });
            } finally {
                this.busy = false;
            }
        },
    },
};
</script>

<style lang="scss">
@import '~bootstrap/scss/_functions.scss';
@import '~bootstrap/scss/_variables.scss';
@import '~bootstrap/scss/mixins/_breakpoints.scss';

.pricing-toggle .v-switch-label {
    padding-top: 0.2em !important;
}

.pricing-toggle .v-switch-core {
    height: 2.35em !important;
}

.pricing-toggle .v-switch-core .v-switch-button {
    top: 0.18em !important;
    height: 2em !important;
}

.pricing-toggle {
    max-width: 210px;
    &_labs {
        max-width: 180px;
    }
}

.pricing-toolbar {
    padding: 0.5rem;
    display: flex;
    flex-direction: column;

    span {
        white-space: nowrap;
    }

    .api-key-wrapper {
        max-width: 50%;

        .api-key-input {
            border-top-right-radius: 0;
            border-bottom-right-radius: 0;
        }

        .copy-api-key {
            border-top-left-radius: 0;
            border-bottom-left-radius: 0;
            margin-right: 0 !important;
        }

        .show-api-key {
            border-radius: 0;
            margin-right: 0 !important;
        }
    }
}

@include media-breakpoint-down(md) {
    .pricing-toolbar {
        .api-key-wrapper {
            max-width: 100%;
        }
    }

    .pricing-toggle {
        max-width: 220px;
        &_labs {
            max-width: 190px;
        }
    }
}
</style>